/* Harrod Outdoors: independently sized, real-product underplanting companion. */
(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (!root || !root.document || root.HarrodUnderplantingCrossSell) return;
  root.HarrodUnderplantingCrossSell = { version: '2026-10-25-inline-border-order', calculator: api };
  const instances = new Map();

  class Underplanting {
    constructor(element) {
      this.el = element;
      this.controller = new AbortController();
      this.request = new api.RequestState();
      this.q = (name) => this.el.querySelector('[data-ho-up-' + name + ']');
      this.select = this.q('collection');
      this.cards = Array.from(this.el.querySelectorAll('[data-ho-up-card]')).filter((card) => card.dataset.hoUpCard);
      this.showAllCards = false;
      this.length = this.q('length');
      this.depth = this.q('depth');
      this.form = this.q('form');
      this.button = this.q('add');
      this.message = '';
      this.messageCart = false;
      this.touched = false;
      this.ready = false;
      this.apiFailed = false;
      this.reconcileController = null;
      this.watchdog = null;
      this.nativeFlyout = null;
      this.errorMessage = '';
      try {
        this.products = JSON.parse(this.q('products').textContent);
        if (!Array.isArray(this.products) || !this.products.length || !this.select || !this.length || !this.depth || !this.form || !this.button) throw new Error('Incomplete collection data.');
        const currency = this.el.dataset.hoUpCurrency || 'GBP';
        this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency });
        this.numbers = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { maximumFractionDigits: 6 });
        const preferred = this.el.dataset.hoUpDefault;
        if (!this.products.some((product) => product.handle === this.select.value) && this.products.some((product) => product.handle === preferred)) this.select.value = preferred;
        this.bind();
        this.updateCollection();
        this.waitForNativeCart();
      } catch (error) {
        if (this.button) this.button.disabled = true;
        if (this.form) this.form.hidden = false;
        const feedback = this.q('feedback');
        if (feedback) { feedback.hidden = false; feedback.textContent = 'The border calculator could not load. Please refresh the page or contact us for help.'; }
      }
    }

    get active() { return !this.controller.signal.aborted && this.el.isConnected; }
    get product() { return this.products.find((product) => product.handle === this.select.value) || this.products[0]; }

    bind() {
      const options = { signal: this.controller.signal };
      this.cards.forEach((card) => card.addEventListener('click', () => {
        if (this.request.isBusy || this.request.phase === 'uncertain') return;
        this.select.value = card.dataset.hoUpCard;
        this.onEdit();
        this.updateCollection();
      }, options));
      const all = this.q('all');
      if (all) all.addEventListener('click', () => { this.showAllCards = !this.showAllCards; this.updateCards(); }, options);
      this.select.addEventListener('change', () => {
        if (this.request.isBusy || this.request.phase === 'uncertain') return;
        this.onEdit();
        this.updateCollection();
      }, options);
      [this.length, this.depth].forEach((field) => {
        field.addEventListener('input', () => { this.onEdit(); this.update(); }, options);
        field.addEventListener('blur', () => { this.touched = true; this.update(); }, options);
      });
      this.form.addEventListener('submit', (event) => this.submit(event), { ...options, capture: true });
      document.addEventListener('addToCartFlyout:complete', (event) => this.nativeCompleted(event), options);
    }

    onEdit() {
      if (this.request.isBusy || this.request.phase === 'uncertain') return;
      this.message = '';
      this.messageCart = false;
      this.errorMessage = '';
    }

    waitForNativeCart() {
      const started = Date.now();
      const check = () => {
        if (!this.active) return this.clearReadiness();
        if (typeof root.AddToCartFlyout === 'function' && root.Shopify && typeof root.Shopify.formatMoney === 'function') {
          this.ready = true;
          this.apiFailed = false;
          this.clearReadiness();
          this.update();
        } else if (Date.now() - started >= 15000) {
          this.apiFailed = true;
          this.clearReadiness();
          this.update();
        }
      };
      this.readinessTimer = root.setInterval(check, 250);
      root.addEventListener('load', check, { once: true, signal: this.controller.signal });
      check();
    }

    clearReadiness() { root.clearInterval(this.readinessTimer); this.readinessTimer = null; }

    updateCollection() {
      if (!this.active) return;
      const product = this.product;
      const selectedCard = this.cards.find(card => card.dataset.hoUpCard === product.handle);
      const selectedWrapper = selectedCard?.closest('[data-ho-up-card-wrap]');
      if (selectedWrapper && this.form.parentElement !== selectedWrapper) selectedWrapper.append(this.form);
      this.form.hidden = false;
      this.q('unit-price').textContent = product.pricePending || !Number.isSafeInteger(product.price) || product.price <= 0 ? 'Price to be confirmed' : this.money.format(product.price / 100) + ' / m²';
      this.q('selected').textContent = product.name + ' border';
      this.updateCards();
      const recipe = Array.from(this.el.querySelectorAll('template[data-ho-up-recipe]')).find((template) => template.dataset.hoUpRecipe === product.handle);
      this.q('plants').replaceChildren(recipe ? recipe.content.cloneNode(true) : document.createTextNode('Please view the collection for its current plant list and supplied sizes.'));
      this.plantQuantities = Array.from(this.q('plants').querySelectorAll('tbody tr')).map((row) => {
        const cell = row.cells[2];
        const perSquareMetre = Number(cell?.textContent.trim());
        return cell && Number.isSafeInteger(perSquareMetre) && perSquareMetre > 0 ? { cell, perSquareMetre } : null;
      }).filter(Boolean);
      const tax = product.taxable === false ? 'No tax charged on this collection' : this.el.dataset.hoUpTaxLabel || 'Any tax is calculated at checkout';
      this.q('tax').textContent = tax;
      this.q('tax').hidden = Boolean(product.pricePending);
      this.q('total-tax').textContent = tax;
      this.update();
    }

    updateCards() {
      const selected = this.cards.findIndex((card) => card.dataset.hoUpCard === this.select.value);
      this.cards.forEach((card, index) => {
        card.setAttribute('aria-pressed', String(index === selected));
        const wrapper = card.closest('[data-ho-up-card-wrap]');
        wrapper?.classList.toggle('ho-up__card-wrap--active', index === selected);
        (wrapper || card).hidden = !this.showAllCards && !(index < (selected > 2 ? 2 : 3) || index === selected);
      });
      const all = this.q('all');
      if (all) {
        all.hidden = this.cards.length <= 3;
        all.setAttribute('aria-expanded', String(this.showAllCards));
        all.textContent = this.showAllCards ? 'Show fewer collections ↑' : 'See all ' + this.cards.length + ' collections ↓';
      }
    }

    update() {
      if (!this.active) return;
      const product = this.product;
      const result = api.calculateCentimetres(this.length.value, this.depth.value);
      const order = api.validateOrder(product, result);
      this.current = { product, result, order, key: result.ok ? api.orderKey(product, result) : null };
      const locked = this.request.isBusy || this.request.phase === 'uncertain';
      [this.length, this.depth, this.select, ...this.cards].forEach((field) => { field.disabled = locked; });
      this.form.setAttribute('aria-busy', String(this.request.isBusy));
      const parsedLength = api.parseCentimetres(this.length.value);
      const parsedDepth = api.parseCentimetres(this.depth.value);
      const lengthOk = parsedLength.ok;
      const depthOk = parsedDepth.ok;
      [['measure-length', lengthOk ? parsedLength.metres : null], ['measure-depth', depthOk ? parsedDepth.metres : null], ['measure-area', result.ok ? result.exactArea : null]].forEach(([name, value]) => {
        const output = this.q(name);
        if (output) output.textContent = value === null ? '—' : this.numbers.format(Number(value));
      });
      const measurement = this.q('measurement');
      if (measurement) {
        [['data-length', lengthOk ? parsedLength.metres : null], ['data-depth', depthOk ? parsedDepth.metres : null]].forEach(([name, value]) => {
          if (value === null) measurement.removeAttribute(name); else measurement.setAttribute(name, String(value));
        });
      }
      (this.plantQuantities || []).forEach(({ cell, perSquareMetre }) => {
        const count = result.ok ? result.quantity * perSquareMetre : null;
        cell.textContent = count !== null && Number.isSafeInteger(count) ? this.numbers.format(count) : '—';
      });
      const plantCopy = this.q('plant-quantity-copy');
      if (plantCopy) plantCopy.textContent = result.ok ? plantCopy.dataset.filled.replace('__AREA__', this.numbers.format(result.quantity)).replace('__COUNT__', this.numbers.format(result.plantCount)) : plantCopy.dataset.empty;
      this.length.setAttribute('aria-invalid', String(this.touched && !lengthOk));
      this.depth.setAttribute('aria-invalid', String(this.touched && !depthOk));
      this.length.setCustomValidity(lengthOk ? '' : this.el.dataset.hoUpLengthError);
      this.depth.setCustomValidity(depthOk ? '' : this.el.dataset.hoUpDepthError);
      this.q('calculation').textContent = result.ok ? this.numbers.format(Number(result.exactArea)) + ' m² measured · ' + this.numbers.format(result.quantity) + ' m² ordered · ' + this.numbers.format(result.plantCount) + ' plants' : 'Enter your measurements to see the area and price.';
      this.q('total').textContent = order.ok ? this.money.format(order.total / 100) : '—';
      const dimensions = this.q('review-dimensions');
      if (dimensions) dimensions.textContent = result.ok ? this.numbers.format(Number(parsedLength.centimetres)) + ' cm × ' + this.numbers.format(Number(parsedDepth.centimetres)) + ' cm' : '';
      const summaryQuantity = this.q('summary-quantity');
      if (summaryQuantity) summaryQuantity.textContent = result.ok ? this.numbers.format(result.quantity) + ' m² · ' + this.numbers.format(result.plantCount) + ' plants in total' : 'Enter your border measurements';
      let error = this.errorMessage;
      if (!error && !product.purchasable) error = product.reason || 'This collection is currently unavailable. Please choose another collection.';
      if (!error && result.ok && !order.ok) error = order.message;
      if (!error && this.touched && !result.ok) error = result.reason === 'too-large' ? 'These measurements are too large to order online. Please contact us for help.' : this.el.dataset.hoUpInvalidCm;
      if (!error && this.apiFailed) error = 'Online ordering could not load. Please refresh the page or contact us for help.';
      this.q('error').textContent = error || '';
      this.q('error').hidden = !error;
      const done = this.current.key && this.request.completedKeys.has(this.current.key);
      this.button.disabled = locked || done || !this.ready || !order.ok;
      this.button.textContent = this.request.isBusy ? (this.request.phase === 'reconciling' ? 'Checking your cart…' : 'Adding your border…') : this.request.phase === 'uncertain' ? 'Check your cart before trying again' : done ? 'Border added to cart' : 'Add to basket';
      const quantity = this.q('quantity');
      const variant = this.q('variant');
      quantity.value = order.ok ? String(result.quantity) : '';
      quantity.disabled = !order.ok || locked;
      variant.value = String(product.variantId || '');
      variant.disabled = !order.ok || locked;
      this.renderFeedback();
    }

    renderFeedback() {
      const feedback = this.q('feedback');
      feedback.hidden = !this.message;
      feedback.replaceChildren(document.createTextNode(this.message));
      if (this.messageCart) {
        feedback.appendChild(document.createTextNode(' '));
        const link = document.createElement('a');
        link.href = this.el.dataset.hoUpCartUrl || '/cart';
        link.textContent = 'View cart';
        feedback.appendChild(link);
      }
    }

    submit(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.request.isBusy || this.request.phase === 'uncertain') return;
      this.touched = true;
      this.update();
      if (!this.current.order.ok || !this.ready || this.button.disabled) {
        if (!this.current.result.ok) (api.parseCentimetres(this.length.value).ok ? this.depth : this.length).focus();
        return;
      }
      const { product, result, key } = this.current;
      const allocation = api.uniqueAllocation(root.crypto);
      if (!this.request.begin({ key, allocation, variantId: product.variantId, quantity: result.quantity, name: product.name })) return;
      this.message = '';
      this.errorMessage = '';
      this.messageCart = false;
      this.update();
      const fields = api.formFields(product, result, allocation);
      try {
        if (this.nativeFlyout) this.nativeFlyout.unload();
        this.nativeFlyout = new root.AddToCartFlyout(fields, {
          atcButton: this.button,
          settings: { moneyFormat: this.el.dataset.hoUpMoneyFormat, cartRedirection: false }
        }, {
          onError: (description) => this.nativeFailed(description, allocation)
        });
        if (this.request.isBusy) this.watchdog = root.setTimeout(() => this.reconcile('We could not confirm the cart update.', allocation), 30000);
      } catch (error) {
        this.reconcile('We could not confirm the cart update.', allocation);
      }
    }

    nativeCompleted(event) {
      if (!this.active) return;
      const data = event.detail && event.detail.responseData;
      const items = data && Array.isArray(data.items) ? data.items : data ? [data] : [];
      if (!this.request.current) return;
      const match = items.find((item) => api.matchesAllocation(item, this.request.current));
      if (!match) return;
      if (Number(match.quantity) !== this.request.current.quantity) {
        this.reconcile('The cart returned a different quantity.', this.request.current.allocation);
        return;
      }
      this.confirmAdded(this.request.current.quantity, false);
    }

    nativeFailed(description, allocation) {
      if (!this.active || !this.request.current || allocation !== this.request.current.allocation) return;
      const message = typeof description === 'string' ? description : 'This border could not be added to your cart.';
      this.reconcile(message, allocation);
    }

    confirmAdded(quantity, partial) {
      if (!this.active || !this.request.current) return;
      root.clearTimeout(this.watchdog);
      this.watchdog = null;
      if (this.reconcileController) this.reconcileController.abort();
      const name = this.request.current.name;
      this.request.confirm();
      this.errorMessage = '';
      this.message = partial ? 'Only ' + this.numbers.format(quantity) + ' m² of the ' + name + ' border was added because the full quantity was unavailable. Review your cart before adding more.' : this.numbers.format(quantity) + ' m² of the ' + name + ' border (' + this.numbers.format(quantity * 5) + ' plants) added to your cart. Your existing items are unchanged.';
      this.messageCart = true;
      this.update();
      // Empire enables the button after it emits completion; reapply our duplicate lock.
      root.queueMicrotask(() => { if (this.active) this.update(); });
    }

    async reconcile(description, allocation) {
      if (!this.active || !this.request.current || allocation !== this.request.current.allocation || this.request.phase === 'reconciling') return;
      root.clearTimeout(this.watchdog);
      this.watchdog = null;
      this.request.phase = 'reconciling';
      this.errorMessage = description;
      this.message = 'Checking whether any underplanting was added…';
      this.messageCart = false;
      this.update();
      const controller = new AbortController();
      this.reconcileController = controller;
      const timeout = root.setTimeout(() => controller.abort(), 10000);
      try {
        const endpoint = api.cartEndpoint(this.el.dataset.hoUpCartUrl || '/cart');
        const response = await root.fetch(endpoint, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, signal: controller.signal });
        if (!response.ok) throw new Error('Cart unavailable.');
        const cart = await response.json();
        if (!this.active || controller.signal.aborted || !this.request.current || allocation !== this.request.current.allocation) return;
        const quantity = (Array.isArray(cart.items) ? cart.items : []).filter((item) => api.matchesAllocation(item, this.request.current)).reduce((sum, item) => sum + Number(item.quantity || 0), 0);
        root.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart }));
        if (quantity > 0) {
          const partial = quantity !== this.request.current.quantity;
          this.confirmAdded(quantity, partial);
        } else {
          this.request.markUncertain();
          this.message = 'No underplanting from this request is currently shown in your cart. Check your cart before refreshing this page to try again.';
          this.messageCart = true;
          this.update();
        }
      } catch (error) {
        if (!this.active || !this.request.current || allocation !== this.request.current.allocation || this.request.phase === 'added') return;
        this.request.markUncertain();
        this.message = 'The result is uncertain. Check your cart before refreshing this page to try again; we have not sent another add request.';
        this.messageCart = true;
        this.update();
      } finally {
        root.clearTimeout(timeout);
        if (this.reconcileController === controller) this.reconcileController = null;
      }
    }

    disconnect() {
      this.controller.abort();
      this.clearReadiness();
      root.clearTimeout(this.watchdog);
      if (this.reconcileController) this.reconcileController.abort();
      // Do not unload a native request while its network result is pending.
      if (this.nativeFlyout && !this.request.isBusy) this.nativeFlyout.unload();
      instances.delete(this.el);
    }
  }

  function scan(container) {
    const elements = [];
    if (container.matches && container.matches('[data-ho-up]')) elements.push(container);
    if (container.querySelectorAll) elements.push(...container.querySelectorAll('[data-ho-up]'));
    elements.forEach((element) => { if (!instances.has(element)) instances.set(element, new Underplanting(element)); });
  }
  const start = () => scan(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    for (const [element, instance] of instances) if (event.target.contains(element)) instance.disconnect();
  });
  const observer = new MutationObserver((records) => {
    if (!records.some((record) => record.removedNodes.length)) return;
    for (const [element, instance] of instances) if (!element.isConnected) instance.disconnect();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';
  const SCALE = 1000000n;
  const MAX = BigInt(Number.MAX_SAFE_INTEGER);
  function decimal(value, places) {
    const digits = value.toString().padStart(places + 1, '0');
    const fraction = digits.slice(-places).replace(/0+$/, '');
    return digits.slice(0, -places) + (fraction ? '.' + fraction : '');
  }
  function parseMetres(value) {
    if (typeof value !== 'string') return { ok: false };
    const text = value.trim();
    if (!text || text.length > 30 || !/^(?:\d+(?:[.,]\d{0,3})?|[.,]\d{1,3})$/.test(text)) return { ok: false };
    const [whole = '', fraction = ''] = text.replace(',', '.').split('.');
    const millimetres = BigInt(whole || '0') * 1000n + BigInt(fraction.padEnd(3, '0'));
    if (millimetres <= 0n) return { ok: false };
    return { ok: true, millimetres, metres: decimal(millimetres, 3) };
  }
  function calculate(lengthText, depthText) {
    const length = parseMetres(lengthText), depth = parseMetres(depthText);
    if (!length.ok || !depth.ok) return { ok: false, reason: 'invalid' };
    const area = length.millimetres * depth.millimetres;
    const quantity = (area + SCALE - 1n) / SCALE;
    if (quantity * 5n > MAX) return { ok: false, reason: 'too-large' };
    return { ok: true, length: length.metres, depth: depth.metres, exactArea: decimal(area, 6), quantity: Number(quantity), plantCount: Number(quantity * 5n) };
  }
  function parseCentimetres(value) {
    if (typeof value !== 'string') return { ok: false };
    const text = value.trim();
    if (!text || text.length > 30 || !/^(?:\d+(?:[.,]\d{0,1})?|[.,]\d)$/.test(text)) return { ok: false };
    const [whole = '', fraction = ''] = text.replace(',', '.').split('.');
    const millimetres = BigInt(whole || '0') * 10n + BigInt(fraction.padEnd(1,'0'));
    if (millimetres <= 0n) return { ok: false };
    return { ok: true, millimetres, centimetres: decimal(millimetres,1), metres: decimal(millimetres,3) };
  }
  function calculateCentimetres(lengthText, depthText) {
    const length = parseCentimetres(lengthText), depth = parseCentimetres(depthText);
    if (!length.ok || !depth.ok) return { ok: false, reason: 'invalid' };
    return calculate(length.metres, depth.metres);
  }
  function validateOrder(product, result) {
    if (!result || !result.ok) return { ok: false, message: 'Enter valid measurements.' };
    if (!product || !product.purchasable || !product.available || product.pricePending || !/^\d+$/.test(String(product.variantId || ''))) return { ok: false, message: product && product.reason || 'This collection is currently unavailable.' };
    if (!Number.isSafeInteger(product.price) || product.price <= 0) return { ok: false, message: 'The price for this collection is being confirmed.' };
    const rules = product.quantityRule || {};
    const minimum = Number.isSafeInteger(rules.min) && rules.min > 0 ? rules.min : 1;
    const increment = Number.isSafeInteger(rules.increment) && rules.increment > 0 ? rules.increment : 1;
    const maximum = rules.max !== null && Number.isSafeInteger(rules.max) && rules.max >= 0 ? rules.max : null;
    if (result.quantity < minimum) return { ok: false, message: 'This collection requires at least ' + minimum + ' m². Adjust your measurements or contact us.' };
    if (maximum !== null && result.quantity > maximum) return { ok: false, message: 'A maximum of ' + maximum + ' m² of this collection can be ordered at once. Please reduce your measurements or contact us.' };
    if (result.quantity % increment !== 0) return { ok: false, message: 'This collection must be ordered in multiples of ' + increment + ' m². Please adjust your measurements or contact us.' };
    const total = BigInt(product.price) * BigInt(result.quantity);
    if (total > MAX) return { ok: false, message: 'This order is too large to calculate online. Please contact us.' };
    return { ok: true, total: Number(total) };
  }
  function orderKey(product, result) { return [String(product.variantId), result.length, result.depth].join('|'); }
  function uniqueAllocation(crypto) { return crypto && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : Date.now().toString(36) + '-' + Math.random().toString(36).slice(2) + '-' + Math.random().toString(36).slice(2); }
  function formFields(product, result, allocation) {
    if (!validateOrder(product, result).ok || !allocation) throw new Error('Invalid underplanting order.');
    return [
      { name: 'id', value: String(product.variantId) },
      { name: 'quantity', value: String(result.quantity) },
      { name: 'properties[Border length (m)]', value: result.length },
      { name: 'properties[Border depth (m)]', value: result.depth },
      { name: 'properties[Measured area (m²)]', value: result.exactArea },
      { name: 'properties[Ordered coverage (m²)]', value: String(result.quantity) },
      { name: 'properties[_Border allocation]', value: allocation }
    ];
  }
  function matchesAllocation(item, request) {
    return Boolean(item && request && String(item.variant_id || item.id) === String(request.variantId) && item.properties && item.properties['_Border allocation'] === request.allocation);
  }
  function cartEndpoint(path) {
    if (!/^\/(?!\/)/.test(path) || /[?#]/.test(path)) throw new Error('Invalid cart route.');
    return path.replace(/\/$/, '').replace(/\.js$/, '') + '.js';
  }
  class RequestState {
    constructor() { this.phase = 'editing'; this.current = null; this.completedKeys = new Set(); }
    get isBusy() { return this.phase === 'pending' || this.phase === 'reconciling'; }
    begin(request) {
      if (this.isBusy || this.phase === 'uncertain' || !request || !request.key || !request.allocation || this.completedKeys.has(request.key)) return false;
      this.current = { ...request }; this.phase = 'pending'; return true;
    }
    confirm() { if (!this.current) return false; this.completedKeys.add(this.current.key); this.phase = 'added'; return true; }
    markUncertain() { this.phase = 'uncertain'; }
  }
  return { parseCentimetres, calculateCentimetres, parseMetres, calculate, validateOrder, orderKey, uniqueAllocation, formFields, matchesAllocation, cartEndpoint, RequestState };
});






